/**
 * SCK-549 — Support Bot floating chat widget.
 *
 * Single source of truth for widget-side constants: feature flag,
 * SSE contract paths (relative — mounted under REACT_APP_API_URL,
 * which already ends in "/api"), storage keys, and copy tokens
 * that the widget needs even when the whole app copy dictionary
 * hasn't loaded yet.
 *
 * Endpoint contract locked in PR-2 (coord):
 *   POST {API_URL}/v1/support-bot/chat/session   → { token: "<opaque>" }
 *        body: { prompt: "<string>", entryPoint: "widget" }
 *   GET  {API_URL}/v1/support-bot/chat/stream?token=<opaque>
 *        SSE stream — EventSource; cannot carry Authorization
 *        header, so the handshake trades caller JWT for an opaque,
 *        single-use, short-TTL token that the stream endpoint accepts.
 */

// Feature flag: widget stays off unless REACT_APP_SUPPORT_WIDGET_ENABLED
// evaluates truthy. Read via helpers/env so the runtime container
// env-injection path (window._env_) also works. Keeps PR-7 shippable
// dark: mount is present on every authenticated page, but invisible
// until the flag flips on per-env.
export const SUPPORT_WIDGET_FLAG = "REACT_APP_SUPPORT_WIDGET_ENABLED";

// Path segments — DO NOT prepend "/api"; API_URL already carries it
// (see memory/feedback_console_api_url.md).
export const SUPPORT_CHAT_HANDSHAKE_PATH = "/v1/support-bot/chat/session";
export const SUPPORT_CHAT_STREAM_PATH = "/v1/support-bot/chat/stream";

// Message role tags used by the widget UI + eventual Gateway payload.
export const ROLE_USER = "user";
export const ROLE_ASSISTANT = "assistant";
export const ROLE_SYSTEM = "system";

// SSE event names the coord proxy is expected to emit. Kept here
// so the hook and any future test-double stay in agreement.
export const SSE_EVENT_CHUNK = "chunk";       // token delta (data: JSON with {text})
export const SSE_EVENT_TOOL = "tool_call";    // support_* tool invoked (data: JSON)
export const SSE_EVENT_TICKET = "ticket";     // support_file_ticket returned {id, sla}
export const SSE_EVENT_DONE = "done";         // stream complete, close EventSource
export const SSE_EVENT_ERROR = "stream_error";// coord signaled a soft error

// Per-viewer draft persistence — a lightweight convenience only;
// message HISTORY is never persisted client-side (SOC 2: no customer
// prompt content held in browser storage beyond the live session).
export const DRAFT_STORAGE_KEY = "c2a.support.draft";

// UI copy — kept inline (not en.js) so PR-7 doesn't touch the shared
// dictionary and force a merge conflict with other pages. Moves to
// en.js in the follow-up polish PR.
export const COPY = {
  fabAria: "Open Clue2App support chat",
  headerTitle: "Support",
  headerSubtitle: "Ask about your apps, builds, or docs.",
  minimize: "Minimize",
  close: "Close chat",
  send: "Send",
  placeholder: "Type a question — Enter to send, Shift+Enter for a new line.",
  emptyTitle: "How can we help?",
  emptyDescription:
    "The support bot can inspect your recent builds, tail your app logs, search docs, and file a support ticket for you when a human needs to jump in.",
  streamError:
    "The support connection dropped. Your last message wasn't answered — try again.",
  disabledHint:
    "Support chat isn't enabled in this environment yet.",
};
